<template>
  <div v-if="isSystemAdmin" class="fixed right-4 top-16 z-50">
    <!-- Toggle Button - styled like the page badges -->
    <button
      class="badge cursor-pointer border bg-white transition-all hover:bg-base-200"
      :class="{ 'border-primary bg-primary/10': isOpen }"
      title="Debug Export (System Admin)"
      @click="togglePopover"
    >
      <m-icon :icon="faBug" class="mr-1 text-xs" />
      Debug
    </button>

    <!-- Popover Panel -->
    <transition
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="opacity-0 translate-y-2"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-100 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-2"
    >
      <div v-if="isOpen" ref="popoverRef" class="absolute right-0 top-8 w-72 rounded-lg bg-base-100 p-4 shadow-xl">
        <div class="mb-3 flex items-center justify-between">
          <h3 class="text-sm font-semibold">Debug Export</h3>
          <button class="btn btn-ghost btn-xs" @click="isOpen = false">
            <m-icon :icon="faTimes" size="sm" />
          </button>
        </div>

        <!-- Request Counter -->
        <div class="mb-3 text-xs text-base-content/70">API Requests: {{ requestCount }}/{{ maxRequests }} captured</div>

        <!-- Truncation Threshold -->
        <div class="mb-3">
          <label class="mb-1 block text-xs font-medium">Truncation Threshold (KB)</label>
          <input
            v-model.number="localThreshold"
            type="number"
            min="1"
            max="1000"
            class="input input-bordered input-sm w-full"
            @change="updateThreshold"
          />
        </div>

        <!-- Export Size Estimate -->
        <div class="mb-3 text-xs text-base-content/70">Estimated export size: ~{{ estimatedSize }}KB</div>

        <!-- Actions -->
        <div class="flex flex-col gap-2">
          <button class="btn btn-secondary btn-sm w-full" @click="openInspector">
            <m-icon :icon="faEdit" size="sm" class="mr-1" />
            State Inspector
          </button>
          <button class="btn btn-primary btn-sm w-full" :disabled="exporting" @click="handleExport">
            <m-icon v-if="exporting" :icon="faSpinner" spin size="sm" class="mr-1" />
            <m-icon v-else :icon="faDownload" size="sm" class="mr-1" />
            Export Debug Data
          </button>
          <button class="btn btn-ghost btn-sm w-full" :disabled="requestCount === 0" @click="handleClear">
            <m-icon :icon="faTrash" size="sm" class="mr-1" />
            Clear API History
          </button>
        </div>
      </div>
    </transition>

    <!-- State Inspector Modal -->
    <MDebugStateInspector :is-open="inspectorOpen" @close="inspectorOpen = false" />
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, onMounted, onUnmounted } from 'vue';
  import { faBug, faTimes, faDownload, faTrash, faSpinner, faEdit } from '@fortawesome/free-solid-svg-icons';
  import { useAccountStore } from '@core/stores/account.store';
  import { STUDIO_CRUD } from '@core/models/permissions';
  import { useDebugExport } from '@shared/composables/useDebugExport';
  import MDebugStateInspector from './MDebugStateInspector.vue';

  const accountStore = useAccountStore();
  const {
    exportDebugData,
    estimateExportSize,
    clearHistory,
    getRequestCount,
    getMaxSize,
    truncationThreshold,
    setTruncationThreshold,
  } = useDebugExport();

  const isOpen = ref(false);
  const exporting = ref(false);
  const inspectorOpen = ref(false);
  const popoverRef = ref<HTMLElement | null>(null);
  const localThreshold = ref(truncationThreshold.value);

  const isSystemAdmin = computed(() => accountStore.hasPermission(STUDIO_CRUD));
  const requestCount = computed(() => getRequestCount());
  const maxRequests = computed(() => getMaxSize());
  const estimatedSize = computed(() => estimateExportSize());

  function togglePopover() {
    isOpen.value = !isOpen.value;
    if (isOpen.value) {
      localThreshold.value = truncationThreshold.value;
    }
  }

  function updateThreshold() {
    setTruncationThreshold(localThreshold.value);
  }

  async function handleExport() {
    exporting.value = true;
    try {
      await exportDebugData();
    } finally {
      exporting.value = false;
      isOpen.value = false;
    }
  }

  function handleClear() {
    clearHistory();
  }

  function openInspector() {
    inspectorOpen.value = true;
    isOpen.value = false;
  }

  // Close popover when clicking outside
  function handleClickOutside(event: MouseEvent) {
    if (popoverRef.value && !popoverRef.value.contains(event.target as Node)) {
      const toggleButton = (event.target as Element).closest('button');
      if (!toggleButton?.title?.includes('Debug Export')) {
        isOpen.value = false;
      }
    }
  }

  onMounted(() => {
    document.addEventListener('click', handleClickOutside);
  });

  onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
  });
</script>
